/* eslint-disable no-unused-vars */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Button, Flex, Text, useDisclosure } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormValidationToast } from '../../../services/actionToast';
import { InputForm } from '../../inputs/forms/InputForm';
import { FormButton } from '../../buttons/FormButton';
import { ReturnButton } from '../../buttons/ReturnButton';
import { getFetchData, useFetch } from '../../../hooks/useFetch';
import { SwitchInputForm } from '../../inputs/forms/SwitchInputForm';
import AlertModal from '../../AlertModal';
import { PageTitle } from '../../PageTitle';
import { FormBox } from '../../formBox';
import { FormHeading } from '../../formHeading';
import { SelectInputForm } from '../../inputs/forms/SelectInputForm';
import { getFormattedLabel } from '../../../utils/FormatFunctions';

interface ModuleWeekFormProps {
  defaultValues?: any;
}

interface FormInputsProps {
  name: string;
  value: any;
  placeholder?: string;
  label: string;
  yup: any;
}

export default function ModuleWeekForm({ defaultValues = {} }: ModuleWeekFormProps) {
  const validationToast = useFormValidationToast();
  const baseUrl = '/v1/admin/weeks';
  const { requestApi, error } = useFetch(baseUrl, { actionFeedback: false });
  const {
    requestApi: reqApiCategories,
    data: dataCategories,
    loading: loadingCategories,
  } = useFetch('/v1/admin/categories');
  const { data: optionsCategories, pagination: paginationCategories } =
    getFetchData(dataCategories);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { itemId, moduleId } = useParams();
  const navigate = useNavigate();
  const yupschema: any = {};
  const defaultValuesSchema: any = {};

  const formInputs: FormInputsProps[] = [
    {
      name: 'name',
      value: defaultValues.name ?? '',
      placeholder: 'Nome da semana',
      label: 'Nome',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'category_id',
      value: defaultValues?.category ? getFormattedLabel(defaultValues?.category) : null,
      placeholder: 'Selecione uma categoria',
      label: 'Categoria',
      yup: Yup.mixed().required('Este campo é obrigatório!'),
    },
    {
      name: 'status',
      value: defaultValues.status ?? true,
      label: 'Status',
      yup: Yup.boolean(),
    },
  ];

  formInputs.forEach((object) => {
    yupschema[object.name] = object.yup;
    defaultValuesSchema[object.name] = object.value;
  });
  const schema = Yup.object().shape(yupschema);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesSchema,
  });

  const name = formInputs.find((field) => field.name === 'name');
  const category = formInputs.find((field) => field.name === 'category_id');
  const status = formInputs.find((field) => field.name === 'status');

  const onSubmit = async (values: any) => {
    const params = {
      ...values,
      category_id: values.category_id.value,
      school_class_id: moduleId,
    };
    await requestApi(
      defaultValues.name ? 'put' : 'post',
      params,
      defaultValues.name ? `${baseUrl}/${itemId}` : null
    );
    onOpen();
  };

  useEffect(() => {
    if (defaultValues.name) {
      reset(defaultValuesSchema);
    }
  }, [defaultValues]);

  return (
    <FormBox>
      <Flex>
        <ReturnButton previousPage={`contents/modules/${moduleId}/weeks`} mb={5} />
        <PageTitle>{defaultValues.name ? 'Editar' : 'Criar'} Semana</PageTitle>
      </Flex>

      <AlertModal
        alertMsg={
          <Text textAlign="center" fontWeight="bold" fontSize="25px">
            {!error
              ? `Semana ${defaultValues.name ? 'editada' : 'criada'} com sucesso!`
              : `Erro ao ${defaultValues.name ? 'editar' : 'criar'} a semana.`}
          </Text>
        }
        msgStatus={error ? 'error' : 'success'}
        closeOnOverlayClick={!!error}
        useSecondaryActions
        isOpenSecondary={isOpen}
        onCloseSecondary={onClose}
        ConfirmButton={
          <Button
            w="250px"
            boxShadow="0px 5px 10px #00000065"
            borderRadius="full"
            onClick={() => navigate(`/contents/modules/${moduleId}/weeks`)}
          >
            Retornar para a listagem
          </Button>
        }
      />

      <form noValidate onSubmit={handleSubmit(onSubmit, validationToast)}>
        <FormHeading>Informações Básicas</FormHeading>

        <Flex gap={5}>
          <Box flex={2}>
            <InputForm
              name={name?.name}
              label={name?.label}
              placeholder={name?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
          </Box>

          <Box flex={2}>
            <SelectInputForm
              name={category?.name}
              label={category?.label}
              options={[
                ...(optionsCategories
                  ?.filter((item) => item.status !== false)
                  .map((item) => getFormattedLabel(item)) ?? []),
              ]}
              isLoading={loadingCategories}
              pagination={paginationCategories}
              getOptions={(filters) => reqApiCategories('get', { params: { ...filters } })}
              placeholder={category?.placeholder}
              isRequired
              control={control}
              errors={errors}
            />
          </Box>

          <Box flex={1}>
            <SwitchInputForm
              name={status?.name}
              label={status?.label}
              errors={errors}
              control={control}
            />
          </Box>
        </Flex>

        <Flex mr="auto">
          <FormButton
            mt={4}
            w="100px"
            isLoading={isSubmitting}
            type="submit"
            textTransform="uppercase"
          >
            Salvar
          </FormButton>
        </Flex>
      </form>
    </FormBox>
  );
}
